<script setup lang="ts">
import { defineProps } from 'vue';
import InfoIcon from '../../icons/InfoIcon.vue';
import IconButton from '../../common/IconButton.vue';
import MenuPoints from '../../icons/MenuPoints.vue';
import { UserRoleType } from '../../../../types/user_role';
import getRoleEmoji from '../../../../utils/user/getRoleEmoji';
import Dropdown from '@/dashboard/vue/components/common/Dropdown/Dropdown.vue';
import Menu from '@/dashboard/vue/components/common/Menu/Menu.vue';
import MenuItem from '@/dashboard/vue/components/common/Menu/MenuItem.vue';
import CloseCircleIcon from '@/dashboard/vue/components/icons/CloseCircleIcon.vue';
import FolderIcon from '@/dashboard/vue/components/icons/FolderIcon.vue';
import { useUserUsageInfoItem } from '@/dashboard/vue/components/workspace/UserUsageInfoItem/useUserUsageInfoItem';
import Divider from '@/dashboard/vue/components/common/Divider.vue';

const props = defineProps<{
  id: number;
  name: string;
  email: string;
  image: string;
  temp: boolean;
  role?: UserRoleType;
}>();

const { menu, canShowEmail, canShowMenu } = useUserUsageInfoItem(props.id, props.role);
</script>

<template>
  <div class="flex items-center justify-between px-xs laptop:h-[46px]">
    <div class="flex laptop:items-center">
      <div class="mr-[10px] h-[20px] w-[20px] overflow-hidden rounded-[50%]">
        <img :src="image" alt="" class="object-cover" />
      </div>

      <div class="flex flex-col">
        <span
          class="text-caption-s flex flex-col whitespace-normal text-black dark:text-gray-500 laptop:flex-row [&>span]:after:mx-xs last:[&>span]:after:content-none laptop:[&>span]:after:content-['/']"
          :class="{ 'text-black dark:text-gray-500': !props.temp, 'text-gray-500 dark:text-gray-700': props.temp }"
        >
          <span v-if="role" class="capitalize">{{ getRoleEmoji(role) }} {{ role }}</span>
          <span>{{ name }}</span>
          <span v-if="canShowEmail">{{ email }}</span>
        </span>

        <div v-if="false" class="flex flex-row items-center gap-xs">
          <button>
            <InfoIcon class="stroke-gray-400 dark:stroke-gray-500" />
          </button>
          <div class="relative h-[4px] w-[320px] overflow-hidden rounded-4 bg-gray-250 dark:bg-gray-800"></div>
        </div>
      </div>
    </div>

    <div v-if="canShowMenu" class="hidden laptop:flex">
      <Dropdown body-class="overflow-visible right-zero mt-[5px]">
        <template #title="{ isOpen }: any">
          <IconButton :active="isOpen" :shadow="false">
            <MenuPoints />
          </IconButton>
        </template>
        <template #body>
          <Menu class="!shadow-none">
            <MenuItem v-for="item in menu" :key="item.label" :action="item.action">
              <div class="flex items-center gap-[10px] whitespace-nowrap">
                <CloseCircleIcon v-if="item.icon === 'CloseCircleIcon'" :class="['h-s w-s', item.iconClass]" />
                <span class="text-caption-s" :class="item.labelClass">{{ item.label }}</span>
              </div>

              <template v-if="item.submenu.length > 0" #menu>
                <Menu>
                  <div v-for="(subItem, index) in item.submenu" :key="subItem.label" class="flex flex-col">
                    <Divider v-if="(index as number) > 0" class="mx-[8px]" />
                    <MenuItem :action="subItem.action">
                      <div class="flex items-center gap-[10px] whitespace-nowrap">
                        <CloseCircleIcon v-if="subItem.icon === 'CloseCircleIcon'" :class="['h-s w-s', subItem.iconClass]" />
                        <FolderIcon v-if="subItem.icon === 'FolderIcon'" :class="['h-s w-s', subItem.iconClass]" />
                        <span class="text-caption-s" :class="subItem.labelClass">{{ subItem.label }}</span>
                      </div>
                    </MenuItem>
                  </div>
                </Menu>
              </template>
            </MenuItem>
          </Menu>
        </template>
      </Dropdown>
    </div>
  </div>
</template>
